import { Close, LayersOutlined } from '@mui/icons-material';
import { IconButton } from '@/components/atoms/Button';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
} from '@mui/material';
import { Text } from '@/components/atoms/text';
import { useTheme } from '@mui/material/styles';
import { ChangeEvent, Fragment, ReactNode } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { BottomSheet } from '@/components/atoms/bottomsheet';
import {
  LayersButton,
  MenuHeader,
  MenuLoading,
} from '@/components/organisms/territorialDashboard/layers/styles';

export type ComparativeMapLayerOption = {
  label: string;
  value: number;
  icon: ReactNode;
};

type ComparativeMapLayersOverlayProps = {
  layers: ComparativeMapLayerOption[];
  loading: boolean;
  selectedLayer: number;
  onLayerChange: (value: number) => void;
  open: boolean;
  onClose: () => void;
};

export const ComparativeMapLayerButton = ({
  onOpen,
  noBorderTop,
}: {
  onOpen: () => void;
  noBorderTop?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <LayersButton
      variant="outlined"
      onClick={onOpen}
      aria-label={t('label.label.active_layer')}
      noBorderTop={noBorderTop}
    >
      <LayersOutlined color="action" />
    </LayersButton>
  );
};

const loadingPlaceholders = Array.from({ length: 7 }, (_, index) => index);

export const ComparativeMapLayersOverlay = ({
  layers,
  loading,
  selectedLayer,
  onLayerChange,
  open,
  onClose,
}: ComparativeMapLayersOverlayProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const layerPanelBottom = `calc(${theme.spacing(1.5)} + 44px + 2 * ${theme.spacing(
    0.75
  )} + ${theme.spacing(1)})`;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onLayerChange(Number(event.target.value));
  };

  const layersContent = (
    <>
      {loading ? (
        <MenuLoading>
          {loadingPlaceholders.map((index) => (
            <Skeleton
              key={index}
              width="100%"
              height={40}
              animation="wave"
              sx={{ transform: 'none' }}
            />
          ))}
        </MenuLoading>
      ) : (
        <FormControl sx={{ width: '100%' }}>
          <RadioGroup name="comparative-map-layers" value={selectedLayer} onChange={handleChange}>
            {layers.map(({ label, value, icon }, index) => (
              <Fragment key={value}>
                <FormControlLabel
                  value={value}
                  control={<Radio color="primary" sx={{ padding: theme.spacing(1) }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                      <Box sx={{ marginRight: theme.spacing(1) }}>{icon}</Box>
                      <Text variant="subtitle2" color="text.primary">
                        {label}
                      </Text>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ margin: 0, justifyContent: 'space-between' }}
                />
                {index !== layers.length - 1 && <Divider variant="fullWidth" />}
              </Fragment>
            ))}
          </RadioGroup>
        </FormControl>
      )}
    </>
  );

  return (
    <>
      <Desktop>
        {open && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              bottom: layerPanelBottom,
              right: theme.spacing(2),
              width: 320,
              maxHeight: 'min(55dvh, 420px)',
              overflow: 'auto',
              padding: theme.spacing(2),
              zIndex: 11,
            }}
          >
            <MenuHeader>
              <Text variant="subtitle1" color="text.primary">
                {t('label.label.active_layer')}
              </Text>
              <IconButton onClick={onClose}>
                <Close color="action" />
              </IconButton>
            </MenuHeader>
            {layersContent}
          </Paper>
        )}
      </Desktop>

      <Mobile>
        <BottomSheet isOpen={open} onClose={onClose} snapPoints={[1, 0.5, 0]} initialSnap={1}>
          <Box sx={{ padding: theme.spacing(2, 3) }}>
            <Text variant="subtitle1" color="text.primary" sx={{ marginBottom: theme.spacing(2) }}>
              {t('label.label.active_layer')}
            </Text>
            {layersContent}
          </Box>
        </BottomSheet>
      </Mobile>
    </>
  );
};
